import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bitcoin, Copy, Check, QrCode } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function PayEntry() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [txId, setTxId] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payment/config").then((r) => setCfg(r.data));
  }, []);

  const copyAddr = () => {
    if (!cfg) return;
    navigator.clipboard.writeText(cfg.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const submitTx = async () => {
    setLoading(true);
    try {
      await api.post("/payment/verify-entry", { tx_id: txId });
      toast.success("Payment verified. Welcome to Bitbook.");
      await refreshUser();
      nav("/feed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const demoMode = async () => {
    setLoading(true);
    try {
      await api.post("/payment/verify-entry", { demo: true });
      toast.success("Demo mode unlocked.");
      await refreshUser();
      nav("/feed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const autoVerify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payment/auto-verify-entry");
      toast.success(`Verified · ${data.amount_btc} BTC received`);
      await refreshUser();
      nav("/feed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const qrUrl = cfg
    ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=bitcoin:${cfg.address}?amount=${cfg.amount_btc}`
    : null;

  return (
    <main className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="font-display font-black text-5xl tracking-tighter uppercase">Unlock access.</h1>
      <p className="mt-3 text-[#0A0A0A]/70 max-w-xl">
        Send <span className="font-mono font-bold">{cfg?.amount_btc ?? "0.001"} BTC</span>
        {cfg?.amount_usd_approx ? <span className="text-[#0A0A0A]/50"> (~${cfg.amount_usd_approx})</span> : null}
        {" "}to the Bitbook wallet below. Paste your transaction ID and we&apos;ll verify on-chain via mempool.space.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-black">
        <div className="p-8 bg-[#0A0A0A] text-[#FDFBF7]">
          <div className="font-mono text-xs uppercase tracking-widest text-[#F7931A] flex items-center gap-2">
            <Bitcoin className="w-4 h-4" /> Send to
          </div>
          <div className="mt-6 flex justify-center">
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="Bitcoin QR"
                data-testid="entry-qr"
                className="border-4 border-white bg-white"
              />
            ) : (
              <div className="w-[280px] h-[280px] flex items-center justify-center border-4 border-white">
                <QrCode className="w-10 h-10 opacity-40" />
              </div>
            )}
          </div>
          <div className="mt-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
              Wallet address
            </div>
            <div
              data-testid="entry-address"
              className="font-mono text-xs break-all bg-white/10 p-3 border border-white/20 mt-2"
            >
              {cfg?.address || "..."}
            </div>
            <button
              data-testid="btn-copy-address"
              onClick={copyAddr}
              className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-white/40 px-3 py-2 hover:bg-white hover:text-black transition-colors"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copied" : "Copy address"}
            </button>
          </div>
        </div>

        <div className="p-8 bg-white">
          <div className="font-mono text-xs uppercase tracking-widest">Step 2 · Verify</div>
          <h2 className="font-display text-3xl font-black mt-3">Paste transaction ID</h2>
          <p className="text-sm text-[#0A0A0A]/70 mt-2">
            After broadcasting your payment, paste the tx hash below.
          </p>
          <input
            value={txId}
            onChange={(e) => setTxId(e.target.value)}
            placeholder="e.g. 4a8b1e..."
            data-testid="entry-tx-input"
            className="mt-6 w-full px-4 py-3 bg-[#FDFBF7] border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#F7931A] focus:ring-offset-2"
          />
          <button
            onClick={submitTx}
            disabled={loading || !txId}
            data-testid="btn-verify-tx"
            className="mt-4 w-full py-4 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal disabled:opacity-50"
          >
            {loading ? "Checking..." : "Verify on-chain"}
          </button>

          <button
            onClick={autoVerify}
            disabled={loading}
            data-testid="btn-auto-verify"
            className="mt-3 w-full py-3 bg-white border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal disabled:opacity-50"
          >
            {loading ? "..." : "Auto-detect payment"}
          </button>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">
            Skip pasting a tx id · we scan mempool.space for you
          </div>

          <div className="mt-8 pt-6 border-t-2 border-black">
            <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
              Just exploring?
            </div>
            <button
              onClick={demoMode}
              disabled={loading}
              data-testid="btn-demo-mode"
              className="mt-3 w-full py-3 bg-white border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal disabled:opacity-50"
            >
              Skip · Continue in demo mode
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
